import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userApi } from '../api';
import './GameOverScreen.css';
import { Achievement } from '../types/achievements';
import './AchievementBadges.css';
const LINK_TO_GROUP = 'https://portal.onersac.com/your-groups/!hqSDkEQIqhYWwfCvFK:415.onersac.com';
interface GameStats {
  username: string;
  level: string;
  score: number;
  totalQuestions: number;
  newly_unlocked_achievements?: Achievement[];
}

const GameOverScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState<GameStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { finalScore, correctAnswers, totalQuestions, newlyUnlockedAchievements, llmAvgPercentage, category } = location.state || { finalScore: 0, correctAnswers: 0, totalQuestions: 0, newlyUnlockedAchievements: [], llmAvgPercentage: 0, category: 'Total Score' };
  const scorePercentage = (correctAnswers / totalQuestions) * 100;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);        
        // Get user profile first
        const profileResponse = await userApi.getProfile();
        if (!profileResponse.data.exists) {
          navigate('/welcome');
          return;
        }

        const username = profileResponse.data.user.username;

        // Get updated stats
        const statsResponse = await userApi.getStats();
        if (statsResponse.data.status === 'success') {
          setStats({
            username,
            level: statsResponse.data.stats.level,
            score: finalScore,
            totalQuestions,
          });
        }
      } catch (err) {
        console.error('Failed to load game stats:', err);
        setError('Failed to load game stats. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [navigate, finalScore, totalQuestions]);

  const getMessage = () => {
    if (scorePercentage >= llmAvgPercentage) {
      return "On average, you were better than the LLMs, with a success rate of " + scorePercentage.toFixed(1) + "% while they had a success rate of " + llmAvgPercentage.toFixed(1) + "%";
    } else {
      return "On average, LLMs had a success rate of " + llmAvgPercentage.toFixed(1) + "% while you had a success rate of " + scorePercentage.toFixed(1) + "%";
    }
  };

  if (isLoading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!stats) return null;

  return (
    <div className="game-over-screen">
      <div className="game-over-content">
        <h1 className="game-over-title">Game Over</h1>

        <div className="final-stats">
          <div className="stat-row">
            <span className="stat-label">Final Score</span>
            <span className="stat-value score">{stats.score}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Success Rate</span>
            <span className="stat-value percentage">{scorePercentage.toFixed(1)}%</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Security Level</span>
            <span className="stat-value level">{stats.level}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Player</span>
            <span className="stat-value player">{stats.username}</span>
          </div>
        </div>

        <div className="performance-summary">
          <h2>Performance Summary</h2>
          <p className="game-over-message">{getMessage()}</p>
        </div>

        <div className="game-over-buttons">
          <button className="play-again-button" onClick={() => navigate('/welcome')}>
            Challenge Again
          </button>
          <button className="leaderboard-button" onClick={() => navigate('/leaderboard', { state: { category } })}>
            View Leaderboard
          </button>
          <button className="profile-button" onClick={() => navigate('/profile')}>
            View Your Profile
          </button>
          {/* <button className="leaderboard-button" onClick={() => window.open(LINK_TO_GROUP, '_blank')}>
            Join the Group!
          </button> */}

        </div>

        {newlyUnlockedAchievements && newlyUnlockedAchievements.length > 0 && (
          <div className="achievements-container">
            <h2>New Achievements Unlocked! 🏆</h2>
            <div className="achievements-grid">
              {newlyUnlockedAchievements.map((achievement: Achievement) => (
                <div key={achievement.id} className="achievement-card new-achievement">
                  <img 
                    src={`/badges/${achievement.id}.png`} 
                    alt={`${achievement.name} badge`} 
                    className="achievement-icon-img"
                  />
                  <span className="achievement-name">{achievement.name}</span>
                  <span className="achievement-separator">•</span>
                  <span className="achievement-description">{achievement.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameOverScreen; 